import { FinancialPeriodPreparationMethod } from "@inrev/common";
import { DateTime } from "luxon";
import type { PeriodSelectorItem } from "../types";

/**
 * Read-only (agent) port of the API's `sortDedupAndSlice`
 * (financial-period.repository.ts): collapse re-uploaded versions of the same period down
 * to the single highest-value one. Sort by period end date DESC, then preparation-method
 * quality ASC (audit → review → compilation → internal), then keep the first row per period.
 *
 * The API dedups within a single period type — each query filters `eq(periodType, …)` before
 * deduping. This runs over the mixed selector list (FYE/YTD/QTD income statements plus balance
 * sheets), so the dedup key combines the period end date with the period-type discriminator
 * (`periodType` for income statements, `statementType` for balance sheets). Keying on date alone
 * would silently drop, e.g., a Q4 QTD that shares a 12/31 period end with an FYE.
 */
const periodDedupKey = (item: PeriodSelectorItem): string => {
	const periodType = item.type === "balance_sheet" ? item.statementType : item.periodType;
	return `${item.type}|${periodType}|${item.date}`;
};

export const dedupToHighestQualityPeriods = <TItem extends PeriodSelectorItem>(
	items: TItem[],
): TItem[] => {
	const sorted = [...items].sort((a, b) => {
		const dateDiff = DateTime.fromISO(b.date).toMillis() - DateTime.fromISO(a.date).toMillis();
		if (dateDiff !== 0) return dateDiff;
		return (
			FinancialPeriodPreparationMethod.qualityRank(a.preparationMethod) -
			FinancialPeriodPreparationMethod.qualityRank(b.preparationMethod)
		);
	});

	const seen = new Set<string>();
	return sorted.filter((item) => {
		const key = periodDedupKey(item);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};
